import { ButtonContent, PendingOverlay } from "@ui/components/internal/ButtonContent";
import { tv, type VariantProps } from "@ui/lib/tv";
import { Button, type ButtonProps } from "@ui/primitives/Button";
import { composeRenderProps } from "@ui/primitives/shared";
import type { ReactNode } from "react";

const standardButton = tv({
  base: [
    "relative inline-flex cursor-pointer items-center justify-center rounded-full",
    "disabled:cursor-not-allowed",
    "data-[pending]:cursor-default",
  ],
  compoundVariants: [
    // === Primary ===
    {
      color: "red",
      hierarchy: "primary",
      class: [
        "bg-red-400 text-white",
        "shadow-[inset_0_0_0_1px_var(--color-red-500),inset_0_-1px_0_0_var(--color-red-600)]",
        "hover:bg-red-500",
        "pressed:bg-red-600 pressed:shadow-none",
        "disabled:bg-red-100 disabled:shadow-none",
      ],
    },
    {
      color: "pink",
      hierarchy: "primary",
      class: [
        "bg-pink-400 text-white",
        "shadow-[inset_0_0_0_1px_var(--color-pink-500),inset_0_-1px_0_0_var(--color-pink-600)]",
        "hover:bg-pink-500",
        "pressed:bg-pink-600 pressed:shadow-none",
        "disabled:bg-pink-100 disabled:shadow-none",
      ],
    },
    {
      color: "green",
      hierarchy: "primary",
      class: [
        "bg-green-500 text-white",
        "shadow-[inset_0_0_0_1px_var(--color-green-600),inset_0_-1px_0_0_var(--color-green-700)]",
        "hover:bg-green-600",
        "pressed:bg-green-700 pressed:shadow-none",
        "disabled:bg-green-100 disabled:shadow-none",
      ],
    },
    {
      // Yellow (community) takes DARK text — yellow-500 is far too light for white text to read.
      // Mirrors the legacy untitled button's validated yellow treatment.
      color: "yellow",
      hierarchy: "primary",
      class: [
        "bg-yellow-500 text-yellow-950",
        "shadow-[inset_0_0_0_1px_var(--color-yellow-600),inset_0_-1px_0_0_var(--color-yellow-700)]",
        "hover:bg-yellow-600",
        "pressed:bg-yellow-700 pressed:shadow-none",
        "disabled:bg-yellow-100 disabled:text-yellow-400 disabled:shadow-none",
      ],
    },
    {
      color: "purple",
      hierarchy: "primary",
      class: [
        "bg-purple-500 text-white",
        "shadow-[inset_0_0_0_1px_var(--color-purple-600),inset_0_-1px_0_0_var(--color-purple-800)]",
        "hover:bg-purple-600",
        "pressed:bg-purple-800 pressed:shadow-none",
        "disabled:bg-purple-100 disabled:shadow-none",
      ],
    },
    {
      color: "neutral",
      hierarchy: "primary",
      class: [
        "bg-white text-neutral-600",
        "shadow-[inset_0_0_0_1px_var(--color-neutral-200),inset_0_-1px_0_0_var(--color-neutral-300)]",
        "hover:bg-neutral-50",
        "pressed:bg-neutral-100 pressed:shadow-none",
        "disabled:bg-neutral-50 disabled:text-neutral-300 disabled:shadow-none",
      ],
    },

    // === Secondary ===
    {
      color: "red",
      hierarchy: "secondary",
      class: [
        "bg-white text-red-400 border border-red-400",
        "hover:bg-red-50",
        "pressed:shadow-[0_0_0_2px_var(--color-red-300)]",
        "disabled:border-neutral-200 disabled:text-neutral-300",
      ],
    },
    {
      color: "green",
      hierarchy: "secondary",
      class: [
        "bg-white text-green-500 border border-green-500",
        "hover:bg-green-50",
        "pressed:shadow-[0_0_0_2px_var(--color-green-200)]",
        "disabled:border-neutral-200 disabled:text-neutral-300",
      ],
    },
    {
      // Label uses yellow-700 (not the -500 border shade) so it stays readable on white.
      color: "yellow",
      hierarchy: "secondary",
      class: [
        "bg-white text-yellow-700 border border-yellow-500",
        "hover:bg-yellow-50",
        "pressed:shadow-[0_0_0_2px_var(--color-yellow-300)]",
        "disabled:border-neutral-200 disabled:text-neutral-300",
      ],
    },
    {
      color: "purple",
      hierarchy: "secondary",
      class: [
        "bg-white text-purple-500 border border-purple-500",
        "hover:bg-purple-50",
        "pressed:shadow-[0_0_0_2px_var(--color-purple-300)]",
        "disabled:border-neutral-200 disabled:text-neutral-300",
      ],
    },

    // === Link ===
    // No fill, no border, no shadow. Mirrors `size` typography but trims horizontal padding
    // so the button reads as a link sitting in the flow. `bg-transparent` and the hover
    // tint come from the color compounds below.
    {
      hierarchy: "link",
      class: ["bg-transparent", "disabled:bg-transparent disabled:text-neutral-300"],
    },
    { hierarchy: "link", size: "44", class: "px-3 [&_svg]:size-4" },
    { hierarchy: "link", size: "56", class: "px-3 [&_svg]:size-4" },
    { hierarchy: "link", size: "60", class: "px-3 [&_svg]:size-4" },
    { hierarchy: "link", size: "64", class: "px-3 [&_svg]:size-4" },
    {
      color: "red",
      hierarchy: "link",
      class: ["text-red-400", "hover:bg-red-50 hover:text-red-500", "pressed:bg-red-100"],
    },
    {
      color: "green",
      hierarchy: "link",
      class: ["text-green-500", "hover:bg-green-50 hover:text-green-600", "pressed:bg-green-100"],
    },
    {
      color: "yellow",
      hierarchy: "link",
      class: ["text-yellow-700", "hover:bg-yellow-50 hover:text-yellow-800", "pressed:bg-yellow-100"],
    },
    {
      color: "purple",
      hierarchy: "link",
      class: ["text-purple-500", "hover:bg-purple-50 hover:text-purple-600", "pressed:bg-purple-100"],
    },
    {
      color: "pink",
      hierarchy: "link",
      class: ["text-pink-400", "hover:bg-pink-50 hover:text-pink-500", "pressed:bg-pink-100"],
    },
    {
      color: "neutral",
      hierarchy: "link",
      class: ["text-neutral-600", "hover:bg-neutral-50 hover:text-neutral-900", "pressed:bg-neutral-100"],
    },
  ],
  defaultVariants: {
    color: "red",
    hierarchy: "primary",
    size: "64",
  },
  variants: {
    color: {
      green: "",
      neutral: "",
      pink: "",
      purple: "",
      red: "",
      yellow: "",
    },
    hierarchy: {
      primary: "",
      secondary: "",
      link: "",
    },
    size: {
      "44": "gap-2 px-6 py-2.5 text-base font-medium [&_svg]:size-4",
      "56": "gap-3 px-7 py-4 text-lg font-normal [&_svg]:size-5",
      "60": "gap-3 px-4 py-[18px] text-lg font-bold [&_svg]:size-5",
      "64": "gap-3 px-8 py-[18px] text-xl font-normal [&_svg]:size-5",
    },
  },
});

type StandardButtonVariants = VariantProps<typeof standardButton>;

export type StandardButtonProps = ButtonProps & {
  color?: StandardButtonVariants["color"];
  hierarchy?: StandardButtonVariants["hierarchy"];
  leadIcon?: ReactNode;
  size?: StandardButtonVariants["size"];
  trailIcon?: ReactNode;
};

export function StandardButton({ color, hierarchy, leadIcon, size, trailIcon, ...props }: StandardButtonProps) {
  return (
    <Button
      {...props}
      className={composeRenderProps(props.className, (className, renderProps) =>
        standardButton({ ...renderProps, color, hierarchy, size, className }),
      )}
    >
      {composeRenderProps(props.children, (children, { isPending }) => (
        <>
          <ButtonContent isPending={isPending} leadIcon={leadIcon} trailIcon={trailIcon}>
            {children}
          </ButtonContent>
          {/* The yellow primary fill takes dark text, so invert the white spinner to keep it visible. */}
          <PendingOverlay invert={color === "yellow"} isPending={isPending} />
        </>
      ))}
    </Button>
  );
}
